import { forwardRef, ForwardedRef, ReactNode } from 'react';
import styled from 'styled-components';
import { backgroundColors, zIndex } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useDrawerContext } from './DrawerContext';

export enum DrawerSize {
  default = 'default',
  large = 'large',
}

export enum DrawerDirection {
  left = 'left',
  right = 'right',
}

// https://zeroheight.com/3fe730a39/v/latest/p/47e3bb-drawer/b/938d10
const drawerWidth = {
  [DrawerSize.default]: '40vw',
  [DrawerSize.large]: `calc(100vw - ${pxToRem(64)})`,
};

const drawerControls = {
  [DrawerDirection.left]: {
    option: DrawerDirection.left,
    calc: 1,
  },
  [DrawerDirection.right]: {
    option: DrawerDirection.right,
    calc: -1,
  },
};

type Props = {
  children: ReactNode;
  size?: DrawerSize;
  direction?: DrawerDirection;
};

type StyledProps = {
  isDrawerOpen: boolean;
  size: DrawerSize;
  direction: DrawerDirection;
};

export const StyledDrawer = styled.div<StyledProps>`
  background-color: ${backgroundColors.backgroundPrimary};
  bottom: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  padding: 0;
  position: absolute;
  transition: transform 0.2s;
  top: 0;
  width: ${({ size }) => drawerWidth[size]};
  z-index: ${zIndex.drawer};

  ${({ size, direction }) =>
    `${drawerControls[direction].option}: calc(-1 * ${drawerWidth[size]})`};

  ${({ size, isDrawerOpen, direction }) =>
    isDrawerOpen &&
    `transform: translateX(calc(${drawerControls[direction].calc} * ${drawerWidth[size]}))`};

  ${({ isDrawerOpen }) => (isDrawerOpen ? `will-change: transform` : '')};
`;

export const Drawer = forwardRef(
  (
    { children, size = DrawerSize.default, direction = DrawerDirection.left, ...props }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { isDrawerOpen } = useDrawerContext();

    return (
      <StyledDrawer
        isDrawerOpen={isDrawerOpen}
        ref={ref}
        size={size}
        direction={direction}
        {...props}
      >
        {children}
      </StyledDrawer>
    );
  },
);
